/*
references:
http://angularscript.com/radial-gauge-angularjs-directive-using-d3-js-ngradialgauge/

calculation for tick rendering
https://oliverbinns.com/articles/D3js-gauge/

*/


import { Component, OnInit, OnDestroy, ViewChild, ElementRef, ViewEncapsulation, Inject, Injector } from '@angular/core';
import { Widget, DataProvider, LogService, GaugeResponseObject, WidegtHelperService } from 'core-dashboard';
import * as d3 from 'd3';
import { TranslateService } from "@ngx-translate/core";

@Component(
    {
        selector: 'radial-gauge-chart',
        template: `<div class='d3-chart' #chart></div>`,
        encapsulation: ViewEncapsulation.None,
        styleUrls: ['./radial.gauge.chart.css']
    })
export class RadialGaugeChart extends Widget implements OnInit, OnDestroy {

    private widgetStyle: any;
    private config: any;
    private dataProvider: any;
    private width: number;
    private height: number;
    private lastHeight: number;
    private lastWidth: number;
    private innerRadius = 0;
    private outerRadius = 0;
    private chartDrawn: boolean = false;
    private d3DataSource = [];
    private structure: [number, number, string];
    private mainChart: any;

    private provider: any;
    private majorGraduationsAngles: any;
    private majorGraduationValues: any
    private currentValue: any;
    private centerX = 0;
    private centerY = 0;
    private majorGraduationMarginTop = 0;
    private minorGraduationLength = 0;
    private majorGraduationLength = 0;
    private oneDegreeAngle = Math.PI / 180;
    private timeInterval: number = 5;
    private lastGaugeStart: number;
    private lastGaugeEndLimit: number;
    private lastGaugeIntervals: number;
    private needlePrvsValue: any;
    private angle = 1;
    private hadErrorinPrvsGetCall = false;
    private gauge_interval_colors = [];
    private responseObject: GaugeResponseObject;

    private defaultStyle: any =
        {
            "background-color": "#F2F2F2",

            "title-font-color": "#efefef",
            "title-font": "Sans-serif",
            "title-font-size": 14,
            "title-background-color": "#404040",

            "error-font-color": "Red",
            "error-font": "Sans-serif",
            "error-font-size": 14,

            "angle": 230,
            "major-intervals": 5,
            "minor-intervals": 10,
            "interval-color": "Gray",
            "majorticks-color": "Black",
            "minorticks-color": "Gray",
            "active-needle-color": "Navy",
            "inactive-needle-color": "Gray",
            "increament-numbers-font-size": 12,
            "numbers-font": "Sans-serif",
            "numbers-font-color": "Black",

            "font-color": "#37474F",
            "font": "Sans-serif",
            "font-size": 12,

            "title-visible": true,
            "value-visible": true,
            "value-position": "middle",
            "margin": {
                "left": 5,
                "top": 5,
                "right": 5,
                "bottom": 5
            }
        }

    //ERROR STRINGS
    private ERROR_TEXT = "Cannot fetch data for chart.";
    private ERROR_INCORRECT_RANGE = "Incorrect range configuration for gauge";
    private ERROR_IN_GAUGE = "Error in gauge chart";
    private STR_VALUE = "Value";
    private STR_OUT_OF_RANGE = "is out of range.";
    private ERROR_MISSING_CONFIGURATION = "RadialGaugeChart:Missing configurations";

    @ViewChild('chart',{static:true}) private chartContainer: ElementRef;
    constructor(@Inject('dataProvider') dataProvider: any,
        @Inject("widgetStyle") widgetStyle: any,
        @Inject("config") _config: any,
        private injector: Injector/*private parameters: ParameterService,private http:Http*/,
        logger: LogService,
        private widgetHelper: WidegtHelperService,
        translateService: TranslateService) {
        super(logger, translateService);
        this.dataProvider = dataProvider;
        this.widgetStyle = widgetStyle;
        this.config = _config;
        this.applyStyles();
    }

    ngOnDestroy() {
        if (this.provider) {
            this.provider.dispose();
        }
    }
    ngAfterViewInit() { }

    ngOnInit() {
        try {
            if (this.config) {
                //this.SetTransaltion();
                if (this.dataProvider && (this.dataProvider.name !== "")) {
                    this.provider = this.injector.get(this.dataProvider.name) as DataProvider;
                    if (this.provider) {
                        this.provider.getData(this);
                    }
                    else {
                        this.logger.logWarning("RadialGaugeChart", "Unable to instantiate dataprovider for chart. DataProvider name: " + this.dataProvider.name)
                        this.Draw(null);
                    }
                }
            } else {
                this.processError(undefined, false, false, false, this.ERROR_MISSING_CONFIGURATION)
                this.logger.logWarning("RadialGaugeChart", " Missing configurations.")
            }
        }
        catch (error) {
            this.logger.logError("RadialGaugeChart", "Error on ngOnInit. Error:" + error);
        }
    }

    SetResponseObject(responsedata: GaugeResponseObject) {
        this.responseObject = responsedata;
        if (this.responseObject === undefined)
            this.responseObject = { DisplayValue: "Nan", PropertyName: "unset", ObjectId: "unset", Unit: "unset", Value: NaN, QualityGood: false, DataPointName: undefined, IsMultiState: false };
    }

    Draw(responsedata: any) {
        try {
            if (responsedata) {
                this.SetResponseObject(responsedata)
                let element = this.chartContainer.nativeElement;
                if (this.responseObject.DataPointName)
                    element.offsetParent.getElementsByClassName('d3-chartTitle')[0].textContent = this.responseObject.DataPointName;
                let data = (this.responseObject != null && this.responseObject != undefined) ? this.responseObject.Value : undefined;
                this.logger.logInfo("RadialGaugeChart", "Received callback from provider.")
                this.currentValue = data != undefined ? parseFloat(data.toString()) : undefined;
                this.createChart();
            } else {
                this.processError(undefined, false, false, false, undefined);
            }
        }
        catch (error) {
            this.logger.logError("RadialGaugeChart", "Error while drawing gauge. Error:" + error);
        }
    }

    private processError(validData, definedvalue, validRange, inRange, customError) {
        try {
            //set error title
            let chartErrorLine1 = "";
            if (customError) {
                chartErrorLine1 = customError;
            }
            else if (!validData || !definedvalue) {
                chartErrorLine1 = this.NAME + ": " + this.ERROR_TEXT;
            }
            else if (!validRange && chartErrorLine1 == "")
                chartErrorLine1 = this.ERROR_INCORRECT_RANGE;
            else if (!inRange && chartErrorLine1 == "")
                chartErrorLine1 = this.STR_VALUE + " (" + this.currentValue.toString() + ") " + this.STR_OUT_OF_RANGE;
            else
                chartErrorLine1 = this.ERROR_IN_GAUGE;

            //read error properties for style
            //this.readErrorProperties();
            let element = this.chartContainer.nativeElement;

            //add error
            this.chartContainer.nativeElement.innerHTML = '';
            this.width = element.offsetWidth;
            this.height = element.offsetHeight

            var svg = d3.select(element).append('svg')
                .style('background-color', this.currentStyle.background_color)
                .attr('width', element.offsetWidth)
                .attr('height', element.offsetHeight);
            // var textsize = this.currentStyle.error_font_size;
            // var textsize = (this.width * (1 + this.currentStyle.error_font_size) / 300);
            // this code should be refactored after implementing label wrap.
            svg.append("text")
                .attr('x', ((this.width) / 2))
                .attr('y', (this.height / 2))
                .attr('text-anchor', 'middle')
                .style('font-size', this.currentStyle.error_font_size)
                .attr('font-size', this.currentStyle.error_font_size)
                .style('font-family', this.currentStyle.error_font)
                .style('fill', this.currentStyle.error_font_color)
                .text(chartErrorLine1)
                .call(this.widgetHelper.textWrap);

            this.hadErrorinPrvsGetCall = true;
            this.logger.logWarning("RadialGaugeChart", chartErrorLine1);
        }
        catch (error) {
            this.logger.logError("RadialGaugeChart", "Error while drawing gauge. Error:" + error);
        }
    }

    private readErrorProperties() {
        //error font color
        if (typeof this.widgetStyle['error-font-color'] !== 'undefined')
            this.currentStyle.error_font_color = this.widgetStyle['error-font-color'];
        else
            this.currentStyle.error_font_color = this.defaultStyle['error-font-color'];

        //error font size
        if (typeof this.widgetStyle['error-font-size'] !== 'undefined')
            this.currentStyle.error_font_size = this.widgetStyle['error-font-size'];
        else
            this.currentStyle.error_font_size = this.defaultStyle['error-font-size'];

        //error font
        if (typeof this.widgetStyle['error-font'] !== 'undefined')
            this.currentStyle.error_font = this.widgetStyle['error-font'];
        else
            this.currentStyle.error_font = this.defaultStyle['error-font'];
    }

    applyStyles() {
        for (let propName in this.defaultStyle) {
            var propertyName = propName.replace(/-/g, "_");
            let propValue = this.widgetStyle[propName];
            if (propValue !== undefined) {
                this.currentStyle[propertyName] = propValue;
            }
            else {
                this.currentStyle[propertyName] = this.defaultStyle[propName];
            }
        }

        //calculate colors
        this.logger.logInfo("RadialGaugeChart", this.currentStyle.interval_color);

        if (this.currentStyle.interval_color == null || this.currentStyle.interval_color == undefined) {
            this.currentStyle.interval_color = "Gray";
            this.logger.logWarning("RadialGaugeChart", "reset the value for interval_color  to default");
        }

        if (this.config) {
            this.currentStyle.start_value = this.config.startValue;
            this.currentStyle.end_value = this.config.endValue;
        }

        //calculate angle
        this.currentStyle.angle = this.currentStyle.angle / 2;

        this.logger.logInfo("RadialGaugeChart", " styles are: " + JSON.stringify(this.currentStyle));
    }

    createChart() {
        try {
            if (this.responseObject) {
                let element = this.chartContainer.nativeElement;
                this.chartContainer.nativeElement.innerHTML = '';
                if (this.currentStyle.start_value < this.currentStyle.end_value) {
                    this.majorGraduationValues = this.getMajorGraduationValues(this.currentStyle.start_value, this.currentStyle.end_value, 2);
                    this.majorGraduationsAngles = this.getMajorGraduationAngles();

                    this.CreateData();
                    var margin = this.currentStyle.margin;

                    this.width = element.offsetWidth - (margin.right + margin.left);
                    this.height = element.offsetHeight - (margin.bottom + margin.top);
                    this.lastHeight = this.height;
                    this.lastWidth = this.width;
                    this.createGauge();
                    this.chartDrawn = true;
                } else {
                    this.processError(true, true, false, false, undefined);
                }

            }
            else {
                this.processError(undefined, false, false, false, undefined);
            }
        }
        catch (error) {
            this.logger.logError("RadialGaugeChart", "Error while drawing gauge. Error:" + error);
        }
    }

    CreateData() {
        this.d3DataSource = [];
        var start = this.currentStyle.start_value;
        for (let interval of this.config.colorlimits) {
            let data: [string, number, number];
            data = [interval.color, start, interval.threshold];
            this.d3DataSource.push(data);
            start = interval.threshold;
        }
    }

    private titleHeight = 0;
    createGauge() {
        //added svg
        let element = this.chartContainer.nativeElement;
        if (element.offsetHeight != 0 && element.offsetWidth != 0) {
            let svg = d3.select(element)
                .style('background-color', this.currentStyle.background_color)
                .append('svg')
                .attr('width', element.offsetWidth)
                .attr('height', element.offsetHeight);
            //.attr('viewBox', '0 0 '+this.width+' '+this.height);;

            this.mainChart = svg.append('g')
                .attr('transform', 'translate(' + this.currentStyle.margin.left + ','
                    + this.currentStyle.margin.top + ')');

            if (this.currentStyle.value_visible)
                this.setValue();

            this.setCalculationParams();

            let yoffsetForGauge = this.GetYOffsetForGauge();

            var translate = "translate(" + this.width / 2 + "," + yoffsetForGauge + ")";

            this.lastGaugeStart = this.currentStyle.start_value;
            this.lastGaugeEndLimit = this.currentStyle.end_value;
            this.lastGaugeIntervals = this.currentStyle.major_intervals;

            var cScale = d3.scaleLinear().domain([this.currentStyle.start_value, this.currentStyle.end_value])
                .range([-1 * this.currentStyle.angle * (this.oneDegreeAngle), this.currentStyle.angle * (this.oneDegreeAngle)]);

            //arc
            var arc = d3.arc()
                .innerRadius(this.innerRadius)
                .outerRadius(this.outerRadius)
                .startAngle(function (d) {
                    let var1 = cScale(d[1]);
                    return var1;
                })
                .endAngle(function (d) {
                    let var1 = cScale(d[2]);
                    return var1;
                });

            //paths
            this.mainChart.selectAll("path")
                .data(this.d3DataSource)
                .enter()
                .append("path")
                .attr("d", arc)
                .style("fill", function (d) {
                    return d[0];
                })
                .attr("transform", translate);


            this.renderMajorGraduations(this.majorGraduationsAngles);
            this.renderMajorGraduationTexts(this.majorGraduationsAngles, this.majorGraduationValues);
            this.renderGraduationNeedle(this.currentValue);

            // if (this.currentStyle.title_visible &&
            //     this.currentStyle.title_position == "top" &&
            //     this.currentStyle.value_visible &&
            //     this.currentStyle.value_position == "middle")
            //     this.AdjustValue();
        }

    }

    private AdjustValue() {
        var value = this.mainChart.selectAll('.mtt-graduationValueText');

        var translateValue = value.attr("transform");

        if (translateValue.indexOf(',') >= 0) {
            var split = value.attr("transform").split(",");;
            var x = parseInt(split[0].split("(")[1]);
            var y = split[1].split(")")[0];

            let newY = parseInt(y) + this.titleHeight + 15;
            let translate = "translate(" + (x) + "," + newY + ")"
            value.attr("transform", translate)
        }
        else if (translateValue.indexOf(' ') >= 0) {
            var split = value.attr("transform").split(" ");;
            var x = parseInt(split[0].split("(")[1]);
            var y = split[1].split(")")[0];

            let newY = parseInt(y) + this.titleHeight + 15;
            let translate = "translate(" + (x) + "," + newY + ")"
            value.attr("transform", translate)
        }
    }

    private GetYOffsetForGauge(): number {
        let yoffset = this.height / 2;

        if (this.currentStyle.title_position == "top")
            yoffset = yoffset + this.titleHeight;

        if (this.currentStyle.value_position == "top")
            yoffset = yoffset + this.valueNodeHeight;

        return yoffset;
    }
    convertToLocaleNumber(numString: string): string {
        let num = Number(numString);
        let currentLang = this.translateService.currentLang;

        return num.toLocaleString(currentLang);
    }

    private valueNodeHeight = 0
    setValue() {

        if (this.currentValue != undefined) {
            let titleTranslate: any;

            this.mainChart.selectAll('.mtt-graduationValueText').remove();
            // var textSize = (this.width * (1 + this.currentStyle.font_size) / 300);
            //var valueVerticalOffset = (Math.round((this.width * 20) / 300));
            var displayText = "";
            if (this.responseObject.QualityGood && (this.responseObject.DisplayValue != "#COM")) {
                if (this.responseObject.IsMultiState) {
                    displayText = this.responseObject.DisplayValue;
                } else {
                    displayText = (parseFloat(this.responseObject.DisplayValue ? this.responseObject.DisplayValue.toString() : this.responseObject.Value.toString()).toFixed(2));
                    displayText = this.convertToLocaleNumber(displayText) + + " " + (this.responseObject.Unit ? this.responseObject.Unit : "");// (parseFloat(this.responseObject.DisplayValue ? this.responseObject.DisplayValue.toString() : this.responseObject.Value.toString()).toFixed(2) + " " + (this.responseObject.Unit ? this.responseObject.Unit : ""));
                }
            } else {
                displayText = "#COM";
            }
            this.mainChart.append("text")
                .attr("class", "mtt-graduationValueText")
                .attr("fill", this.currentStyle.font_color)
                .attr("text-anchor", "middle")
                .attr("font-size", this.currentStyle.font_size)
                .style("font-size", this.currentStyle.font_size)
                .attr("font-family", this.currentStyle.font)
                .text('[ ' + displayText + ' ]');

            var valuenode = this.mainChart.selectAll('.mtt-graduationValueText');
            this.valueNodeHeight = valuenode.node().getBBox().height;

            if (this.currentStyle.value_position == "top") {
                // -5 for height adjustment as there is default padding at top of title
                let yoffset = this.valueNodeHeight

                titleTranslate = "translate(" + (this.width / 2) + "," + (yoffset) + ")"
                this.height = this.height - this.valueNodeHeight;
            }
            else if (this.currentStyle.value_position == "bottom") {
                let yOffset = this.height;

                titleTranslate = "translate(" + (this.width / 2) + "," + (yOffset) + ")"
                this.height = this.height - this.valueNodeHeight;
            }
            else {
                // y translate =  half of height+ valuenode height + margin of 10
                titleTranslate = "translate(" + (this.width / 2) + "," + ((this.height / 2) + this.valueNodeHeight + 25) + ")"
            }
            valuenode.attr("transform", titleTranslate);
        }

    }

    setCalculationParams() {
        this.centerX = this.width / 2;
        this.centerY = this.GetYOffsetForGauge();
        var minParam = Math.min(this.width, this.height);

        this.majorGraduationMarginTop = Math.round((minParam * 2) / 300);
        this.majorGraduationLength = Math.round((minParam * 16) / 300);
        this.minorGraduationLength = Math.round((minParam * 10) / 300);


        this.innerRadius = Math.round((minParam * 130) / 300);
        this.outerRadius = Math.round((minParam * 145) / 300);
    }

    renderMinorGraduations(majorGraduationsAngles, indexMajor) {
        //render value for minor Graduation
        var minorGraduationsAngles = [];

        if (indexMajor > 0) {
            var minScale = majorGraduationsAngles[indexMajor - 1];
            var maxScale = majorGraduationsAngles[indexMajor];
            var scaleRange = maxScale - minScale;

            for (var i = 1; i < this.currentStyle.minor_intervals; i++) {
                var scaleValue = minScale + i * scaleRange / this.currentStyle.minor_intervals;
                minorGraduationsAngles.push(scaleValue);
            }

            //Render Minor Graduations
            minorGraduationsAngles.forEach((pValue, indexMinor) => {
                var cos1Adj = Math.round(Math.cos((90 - pValue) * this.oneDegreeAngle) * (this.innerRadius - this.majorGraduationMarginTop - this.minorGraduationLength));
                var sin1Adj = Math.round(Math.sin((90 - pValue) * this.oneDegreeAngle) * (this.innerRadius - this.majorGraduationMarginTop - this.minorGraduationLength));
                var cos2Adj = Math.round(Math.cos((90 - pValue) * this.oneDegreeAngle) * (this.innerRadius - this.majorGraduationMarginTop));
                var sin2Adj = Math.round(Math.sin((90 - pValue) * this.oneDegreeAngle) * (this.innerRadius - this.majorGraduationMarginTop));
                var x1 = this.centerX + cos1Adj;
                var y1 = this.centerY + sin1Adj * -1;
                var x2 = this.centerX + cos2Adj;
                var y2 = this.centerY + sin2Adj * -1;
                this.mainChart.append("svg:line")
                    .attr("x1", x1)
                    .attr("y1", y1)
                    .attr("x2", x2)
                    .attr("y2", y2)
                    .style("stroke", this.currentStyle.minorticks_color);
            });
        }
    }

    //render text for Major Graduation
    private renderMajorGraduationTexts(majorGraduationsAngles, majorGraduationValues) {
        var textVerticalPadding = 2;
        var textHorizontalPadding = 2;

        var lastGraduationValue = majorGraduationValues[majorGraduationValues.length - 1];
        //   var textSize = (this.width * (1 + this.currentStyle.increament_numbers_font_size) / 300); //change 6 in case to change text size



        for (var i = 0; i < majorGraduationsAngles.length; i++) {
            var dummyText = this.mainChart.append("text")
                .attr("class", "dummyText")
                .attr("x", this.centerX)
                .attr("y", this.centerY)
                .attr("fill", "transparent")
                .attr("text-anchor", "middle")
                .attr("font-size", this.currentStyle.increament_numbers_font_size)
                .style("font-size", this.currentStyle.increament_numbers_font_size)
                .attr("font-family", this.currentStyle.numbers_font)
                //.text(parseFloat(majorGraduationValues[i]));
                .text(this.convertToLocaleNumber((parseFloat(majorGraduationValues[i])).toString()));

            var textWidth = dummyText.node().getBBox().width;
            this.mainChart.selectAll('.dummyText').remove();
            var angle = majorGraduationsAngles[i];
            var cos1Adj = Math.round(Math.cos((90 - angle) * this.oneDegreeAngle) *
                (this.innerRadius - this.majorGraduationMarginTop - this.majorGraduationLength - textHorizontalPadding));

            var sin1Adj = Math.round(Math.sin((90 - angle) * this.oneDegreeAngle) *
                (this.innerRadius - this.majorGraduationMarginTop - this.majorGraduationLength - textVerticalPadding));

            var sin1Factor = 1;
            if (sin1Adj < 0) sin1Factor = 1.1;
            if (sin1Adj > 0) sin1Factor = 0.9;
            if (cos1Adj > 0) {
                if (angle > 0 && angle < 45) {
                    cos1Adj -= textWidth / 2;
                } else {
                    cos1Adj -= textWidth;
                }
            }
            if (cos1Adj < 0) {
                if (angle < 0 && angle > -45) {
                    cos1Adj -= textWidth / 2;
                }
            }
            if (cos1Adj == 0) {
                cos1Adj -= angle == 0 ? textWidth / 4 : textWidth / 2;
            }

            var x1 = this.centerX + cos1Adj;
            var y1 = this.centerY + sin1Adj * sin1Factor * -1;

            this.mainChart.append("text")
                .attr("class", "mtt-majorGraduationText")
                .attr("font-size", this.currentStyle.increament_numbers_font_size)
                .style("font-size", this.currentStyle.increament_numbers_font_size)
                .attr("font-family", this.currentStyle.numbers_font)
                .attr("text-align", "center")
                .attr("x", x1)
                .attr("dy", y1)
                .attr("fill", this.currentStyle.numbers_font_color)
                //.text(parseFloat(majorGraduationValues[i]));
                .text(this.convertToLocaleNumber((parseFloat(majorGraduationValues[i])).toString()));

        }
    }

    private
    renderGraduationNeedle(value) {
        var isInactive: boolean = false;
        if (this.currentValue == null ||
            this.currentValue == undefined ||
            this.currentValue < this.currentStyle.start_value ||
            this.currentValue > this.currentStyle.end_value || !(this.responseObject.QualityGood) || this.responseObject.DisplayValue == "#COM") {
            value = this.currentValue < this.currentStyle.start_value ? this.currentStyle.start_value : this.currentStyle.end_value;
            isInactive = true;
        }

        var needle = undefined;

        //add circle
        var circle = this.mainChart.selectAll('.mtt-graduation-needle-center');
        if (circle.node() === null || circle.node() === undefined) {
            var circleRadius = (this.width * 4) / 300;

            var circle = this.mainChart.append("circle")
                .attr("r", circleRadius)
                .attr("cy", this.centerY)
                .attr("cx", this.centerX)
                .attr("fill", (isInactive ? this.currentStyle.inactive_needle_color : this.currentStyle.active_needle_color))
                .attr("class", "mtt-graduation-needle-center");
        }

        //get new needle angle
        var needleAngle = this.getNewAngle(value);

        this.mainChart.selectAll('.mtt-graduation-needle').remove();
        var needleLen = this.innerRadius;
        var needleRadius = (this.width * 2.5) / 300;

        var lineData = [
            [needleRadius, 0],
            [0, -needleLen],
            [-needleRadius, 0],
            [needleRadius, 0]
        ];

        let needleValueTextSize = 1;
        var pointerLine = d3.line();
        var pg = this.mainChart.append('g').data([lineData])
            .attr('class', 'mtt-graduation-needle')
            .style("fill", (isInactive ? this.currentStyle.inactive_needle_color : this.currentStyle.active_needle_color))
            .attr('transform', 'translate(' + this.centerX + ',' + this.centerY + ')');

        circle.attr("fill", (isInactive ? this.currentStyle.inactive_needle_color : this.currentStyle.active_needle_color))
        needle = pg.append('path')
            .attr('d', pointerLine)
            .attr('transform', 'rotate(' + needleAngle + ')');

        d3.select('.mtt-graduation-needle').select('line')
            .transition()
            .duration(3000);
    }

    private rotateNeedle(sampleAngle) {
        return "rotate(" + sampleAngle + "," + this.centerX + "," + this.centerY + ")";
    }

    private getNewAngle(pValue) {
        //get new angle for new needle value
        var scale = d3.scaleLinear().range([0, 1]).domain([this.currentStyle.start_value, this.currentStyle.end_value]);
        var ratio = scale(pValue);
        var scaleRange = 2 * this.currentStyle.angle;
        var minScale = -1 * this.currentStyle.angle;
        var newAngle = minScale + (ratio * scaleRange);
        return newAngle;
    }

    private getMajorGraduationAngles(): any[] {
        //calculate values for Major angles
        var scaleRange = 2 * this.currentStyle.angle;
        var minScale = -1 * this.currentStyle.angle;
        var graduationsAngles = [];
        for (var i = 0; i <= this.currentStyle.major_intervals; i++) {
            var scaleValue = minScale + i * scaleRange / (this.currentStyle.major_intervals);
            graduationsAngles.push(scaleValue);
        }
        return graduationsAngles;
    }

    getMajorGraduationValues(pMinLimit, pMaxLimit, pPrecision): any[] {
        //calculate values for Major Graduation
        var scaleRange = pMaxLimit - pMinLimit;
        var majorGraduationValues = [];
        for (var i = 0; i <= this.currentStyle.major_intervals; i++) {
            var scaleValue = pMinLimit + i * scaleRange / (this.currentStyle.major_intervals);
            majorGraduationValues.push(parseFloat(scaleValue).toFixed(pPrecision));
        }
        return majorGraduationValues;
    }

    renderMajorGraduations(majorGraduationsAngles) {
        //Render Major Graduations
        majorGraduationsAngles.forEach((pValue, index) => {
            var cos1Adj = Math.round(Math.cos((90 - pValue) * this.oneDegreeAngle) * (this.innerRadius - this.majorGraduationMarginTop - this.majorGraduationLength));
            var sin1Adj = Math.round(Math.sin((90 - pValue) * this.oneDegreeAngle) * (this.innerRadius - this.majorGraduationMarginTop - this.majorGraduationLength));
            var cos2Adj = Math.round(Math.cos((90 - pValue) * this.oneDegreeAngle) * (this.innerRadius - this.majorGraduationMarginTop));
            var sin2Adj = Math.round(Math.sin((90 - pValue) * this.oneDegreeAngle) * (this.innerRadius - this.majorGraduationMarginTop));
            var x1 = this.centerX + cos1Adj;
            var y1 = this.centerY + sin1Adj * -1;
            var x2 = this.centerX + cos2Adj;
            var y2 = this.centerY + sin2Adj * -1;

            this.mainChart.append("svg:line")
                .attr("x1", x1)
                .attr("y1", y1)
                .attr("x2", x2)
                .attr("y2", y2)
                .style("stroke", this.currentStyle.majorticks_color);

            this.renderMinorGraduations(majorGraduationsAngles, index);
        });
    }

    private readMinRequiredProperties() {
        //read background
        if (typeof this.widgetStyle['background-color'] !== 'undefined')
            this.currentStyle.background_color = this.widgetStyle['background-color'];
        else
            this.currentStyle.background_color = this.defaultStyle['background-color'];

        //gauge start value
        if (typeof this.widgetStyle['start-value'] !== 'undefined')
            this.currentStyle.start_value = this.widgetStyle['start-value'];
        else
            this.currentStyle.start_value = this.defaultStyle['start-value'];

        //gauge end value
        if (typeof this.widgetStyle['end-value'] !== 'undefined')
            this.currentStyle.end_value = this.widgetStyle['end-value'];
        else
            this.currentStyle.end_value = this.defaultStyle['end-value'];

    }

    InitiateDraw(data: any) {
        var validData = data != null && data != undefined;

        //this.readMinRequiredProperties();

        //check if gauge start and end values are correct (this is needed to draw basic control)
        let validRange = this.currentStyle.start_value < this.currentStyle.end_value;

        //check if data is a valid number
        this.currentValue = validData ? parseFloat(data.toString()) : undefined;

        let definedvalue = this.currentValue !== undefined;

        var inRange = (this.currentValue <= this.currentStyle.end_value && this.currentValue >= this.currentStyle.start_value);

        if (validRange && validData && inRange && definedvalue) {

            //render chart
            if (this.currentStyle.end_value == this.lastGaugeEndLimit &&
                this.currentStyle.start_value == this.lastGaugeStart &&
                this.currentStyle.major_intervals == this.lastGaugeIntervals &&
                !this.hadErrorinPrvsGetCall) {
                this.renderGraduationNeedle(this.currentValue)
            }
            else {
                this.createChart();
            }
            this.hadErrorinPrvsGetCall = false;

            //this.renderGraduationNeedle(this.currentValue);
        }
        else {
            this.processError(validData, definedvalue, validRange, inRange, undefined);
        }

    }

    Update(responsedata: any) {
        try {
            if (this.chartDrawn) {
                this.SetResponseObject(responsedata)
                this.logger.logInfo("RadialGaugeChart", "Update called received from provider");
                //only update needle value
                var displayText = "";
                if (this.responseObject.QualityGood && (this.responseObject.DisplayValue != "#COM")) {
                    if (this.responseObject.IsMultiState) {
                        displayText = this.responseObject.DisplayValue;
                    } else {
                        displayText = (parseFloat(this.responseObject.DisplayValue ? this.responseObject.DisplayValue.toString() : this.responseObject.Value.toString()).toFixed(2));
                        displayText = this.convertToLocaleNumber(displayText) + " " + (this.responseObject.Unit ? this.responseObject.Unit : "");// (parseFloat(this.responseObject.DisplayValue ? this.responseObject.DisplayValue.toString() : this.responseObject.Value.toString()).toFixed(2) + " " + (this.responseObject.Unit ? this.responseObject.Unit : ""));
                    }
                } else {
                    displayText = "#COM";
                }
                let data = (this.responseObject != null && this.responseObject != undefined) ? this.responseObject.Value : undefined;
                //check if data is not null
                var validData = data != null && data != undefined;
                this.currentValue = validData ? parseFloat(data.toString()) : undefined;
                //   this.InitiateDraw(data);
                this.renderGraduationNeedle(data);
                if (this.currentStyle.value_visible) {
                    var valuestring = this.mainChart.selectAll('.mtt-graduationValueText');
                    if (valuestring && (this.responseObject.Value != null || this.responseObject.Value != undefined))
                        valuestring.text('[ ' + displayText + ' ]');
                }
            }
        }
        catch (error) {
            this.logger.logError("RadialGaugeChart", "Error while updating gauge value. Error: " + error)
        }
    }

    getConfig() {
        return this.dataProvider.privateConfig;
    }
}


